import { useState } from 'react';
import { supabasePortal as supabase } from '../lib/supabase';
import { i18n, LANG_LABELS, detectLang } from './portalI18n';

const CARD_STYLE = {
  background: 'white',
  borderRadius: 20,
  padding: '48px 40px',
  maxWidth: 420,
  width: '90%',
  textAlign: 'center',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
};

export default function PortalLoginPage({ onSuccess }) {
  const [lang, setLang] = useState(detectLang);
  const t = i18n[lang];

  // 'login' | 'forgot' | 'sent'
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const { data, error: authErr } = await supabase.auth.signInWithPassword({ email, password });
      if (authErr) throw authErr;
      onSuccess(data.session);
    } catch (err) {
      setError(t.loginError);
    } finally {
      setBusy(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const redirectTo = window.location.origin + '/portal';
      const { error: resetErr } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
      if (resetErr) throw resetErr;
      setMode('sent');
    } catch (err) {
      setError(err.message || t.loginError);
    } finally {
      setBusy(false);
    }
  };

  const LangSelector = () => (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 24 }}>
      {Object.entries(LANG_LABELS).map(([code, label]) => (
        <button
          key={code}
          onClick={() => { setLang(code); localStorage.setItem('portal_lang', code); }}
          style={{
            padding: '4px 10px', borderRadius: 20, border: '1px solid',
            borderColor: lang === code ? '#4338ca' : '#e2e8f0',
            background: lang === code ? '#4338ca' : 'white',
            color: lang === code ? 'white' : '#64748b',
            cursor: 'pointer', fontSize: 12, fontWeight: 600,
          }}
        >{label}</button>
      ))}
    </div>
  );

  const textAlign = lang === 'en' ? 'left' : 'right';

  // ── Forgot password — email sent confirmation ──
  if (mode === 'sent') {
    return (
      <div style={{ ...pageStyle, direction: t.dir }}>
        <div style={CARD_STYLE}>
          <LangSelector />
          <div style={{ fontSize: 48, marginBottom: 12 }}>📧</div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', margin: '0 0 12px' }}>{t.resetSent}</h1>
          <p style={{ fontSize: 14, color: '#475569', marginBottom: 24 }}>{t.resetSentMsg(email)}</p>
          <button onClick={() => { setMode('login'); setError(''); }} style={backBtnStyle}>
            ← {t.backToLogin}
          </button>
        </div>
      </div>
    );
  }

  // ── Forgot password — email input ──
  if (mode === 'forgot') {
    return (
      <div style={{ ...pageStyle, direction: t.dir }}>
        <div style={CARD_STYLE}>
          <LangSelector />
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔑</div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', margin: '0 0 6px' }}>{t.forgotTitle}</h1>
          <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 28px' }}>{t.forgotSubtitle}</p>
          <form onSubmit={handleForgot} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ textAlign }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                {t.emailLabel}
              </label>
              <input
                type="email" required value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={t.emailPlaceholder}
                style={inputStyle} dir="ltr"
              />
            </div>
            {error && <div style={errorStyle}>{error}</div>}
            <button type="submit" disabled={busy} style={submitBtnStyle(busy)}>
              {busy ? t.sendingReset : t.sendResetBtn}
            </button>
          </form>
          <button onClick={() => { setMode('login'); setError(''); }} style={backBtnStyle}>
            ← {t.backToLogin}
          </button>
        </div>
      </div>
    );
  }

  // ── Login ──
  return (
    <div style={{ ...pageStyle, direction: t.dir }}>
      <div style={CARD_STYLE}>
        <LangSelector />

        <div style={{ fontSize: 48, marginBottom: 12 }}>🏦</div>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', margin: '0 0 6px' }}>
          {t.loginTitle}
        </h1>
        <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 28px' }}>
          {t.loginSubtitle}
        </p>

        <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ textAlign }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
              {t.emailLabel}
            </label>
            <input
              type="email" required value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={t.emailPlaceholder}
              style={inputStyle} dir="ltr"
            />
          </div>
          <div style={{ textAlign }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
              {t.passwordLabel}
            </label>
            <input
              type="password" required value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder={t.passwordPlaceholder}
              style={inputStyle} dir="ltr"
            />
          </div>

          {error && <div style={errorStyle}>{error}</div>}

          <button type="submit" disabled={busy} style={submitBtnStyle(busy)}>
            {busy ? t.loggingIn : t.loginBtn}
          </button>
        </form>

        <button onClick={() => { setMode('forgot'); setError(''); }} style={backBtnStyle}>
          {t.forgotPassword}
        </button>

        <div style={{ textAlign: 'center', marginTop: 16, paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
          <a href="/portal?mode=self-register" style={{ color: '#4338ca', fontSize: 13, textDecoration: 'none', fontWeight: 700 }}>
            לקוח חדש בפורטל? הרשמה עצמאית
          </a>
        </div>
      </div>
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'linear-gradient(135deg, #1e3a8a 0%, #4338ca 100%)',
  fontFamily: "'Segoe UI', -apple-system, sans-serif",
};

const inputStyle = {
  width: '100%', padding: '10px 14px',
  border: '1.5px solid #e2e8f0', borderRadius: 10,
  fontSize: 14, outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};

const errorStyle = {
  background: '#fee2e2', color: '#991b1b', borderRadius: 8, padding: '10px 14px', fontSize: 13,
};

const submitBtnStyle = (busy) => ({
  padding: '13px', borderRadius: 10, border: 'none',
  background: busy ? '#a5b4fc' : '#4338ca',
  color: 'white', fontSize: 15, fontWeight: 700,
  cursor: busy ? 'default' : 'pointer', transition: 'background 0.15s',
});

const backBtnStyle = {
  display: 'block', marginTop: 18, background: 'none', border: 'none',
  color: '#6366f1', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: 0, width: '100%',
};
